/**
 * Credenciais do sistema host. Tres formas de chegar:
 *
 * 1. URL params: ?apiKey=fk_...&userId=...&userEmail=...
 *    Util quando o host embarca via iframe — passa por query string.
 *    Quando detectado, salva em sessionStorage e remove da URL.
 *
 * 2. localStorage: usuario colou na tela /credenciais
 *    Persiste entre reloads. Pra dev/admin standalone.
 *
 * 3. Em ambiente embed seguro (futuro), via postMessage.
 *
 * Sem credenciais validas, app redireciona pra /credenciais.
 */

const STORAGE_KEY = 'filmaker.credenciais';
const SESSION_KEY = 'filmaker.credenciais.session';

export interface Credenciais {
  apiKey: string;
  userId: string;
  userEmail?: string | null;
}

function consumirUrlParams(): Credenciais | null {
  const params = new URLSearchParams(window.location.search);
  const apiKey = params.get('apiKey');
  const userId = params.get('userId');
  if (!apiKey || !userId) return null;

  const userEmail = params.get('userEmail') ?? undefined;
  const cred: Credenciais = { apiKey, userId, userEmail };

  // Remove da URL pra nao deixar credenciais no historico/referrer
  params.delete('apiKey');
  params.delete('userId');
  params.delete('userEmail');
  const limpo = params.toString();
  const newUrl = window.location.pathname + (limpo ? `?${limpo}` : '') + window.location.hash;
  window.history.replaceState({}, '', newUrl);

  // Salva em session (dura ate fechar a aba) — embed nao deve ficar
  // gravado em local persistente
  sessionStorage.setItem(SESSION_KEY, JSON.stringify(cred));
  return cred;
}

export function getCredenciais(): Credenciais | null {
  // Prioridade: URL params -> session -> local
  const fromUrl = consumirUrlParams();
  if (fromUrl) return fromUrl;

  const fromSession = sessionStorage.getItem(SESSION_KEY);
  if (fromSession) {
    try { return JSON.parse(fromSession); } catch { /* ignora */ }
  }
  const fromLocal = localStorage.getItem(STORAGE_KEY);
  if (fromLocal) {
    try { return JSON.parse(fromLocal); } catch { /* ignora */ }
  }
  return null;
}

export function setCredenciais(c: Credenciais, persistir = true): void {
  if (persistir) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(c));
  } else {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(c));
  }
}

export function clearCredenciais(): void {
  localStorage.removeItem(STORAGE_KEY);
  sessionStorage.removeItem(SESSION_KEY);
}
